import type { ReactNode } from 'react';
import { useOnlineStatus } from '../../hooks/useUIResilience';

/**
 * Скелетон загрузки карточки — повторяет форму карточки списка.
 * Используется как замена спиннера при первичной загрузке данных.
 */
export function SkeletonCard({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-2.5 animate-pulse">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="bg-tg-theme-sec-bg/60 p-3.5 rounded-[20px] border border-black/5 dark:border-white/5 flex items-center gap-3"
        >
          <div className="w-10 h-10 rounded-xl bg-black/10 dark:bg-white/10 shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-3/5 bg-black/10 dark:bg-white/10 rounded" />
            <div className="h-2.5 w-2/5 bg-black/5 dark:bg-white/5 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Скелетон таблицы — для админских экранов с табличным представлением данных.
 */
export function SkeletonTable({ rows = 4, cols = 3 }: { rows?: number; cols?: number }) {
  return (
    <div className="animate-pulse space-y-2">
      {/* Заголовок */}
      <div className="flex gap-4 px-4 py-3">
        {Array.from({ length: cols }).map((_, i) => (
          <div key={i} className="h-3 flex-1 bg-black/10 dark:bg-white/10 rounded" />
        ))}
      </div>
      {/* Строки */}
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-4 px-4 py-3 border-t border-black/5 dark:border-white/5">
          {Array.from({ length: cols }).map((_, j) => (
            <div key={j} className="h-3 flex-1 bg-black/5 dark:bg-white/5 rounded" />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Пустое состояние экрана — отображается когда данные загружены, но список пуст.
 */
export function EmptyState({
  icon = '📭',
  title,
  description,
  action,
}: {
  icon?: string;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center text-center p-8 bg-tg-theme-sec-bg/40 backdrop-blur-sm rounded-[24px] border border-black/5 dark:border-white/5 mt-4">
      <div className="text-4xl mb-3">{icon}</div>
      <div className="font-bold text-xs text-tg-theme-text uppercase tracking-wider mb-1">
        {title}
      </div>
      {description && (
        <p className="text-[10px] text-tg-theme-hint max-w-[220px] mb-4 leading-relaxed">
          {description}
        </p>
      )}
      {action}
    </div>
  );
}

/**
 * Состояние ошибки — отображается при сбое запроса к API с кнопкой «Повторить».
 */
export function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center text-center p-6 bg-status-error/10 rounded-[24px] border border-status-error/10 mt-4">
      <div className="text-2xl mb-2">⚠️</div>
      <div className="font-bold text-xs text-status-error uppercase tracking-wider mb-2">
        {message}
      </div>
      {onRetry && (
        <button
          onClick={onRetry}
          className="bg-status-error/20 text-status-error px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider hover:bg-status-error/30 active:scale-95 transition-all touch-target"
        >
          Повторить попытку
        </button>
      )}
    </div>
  );
}

/**
 * Баннер оффлайн-режима — неблокирующий, фиксированный сверху экрана.
 * Автоматически скрывается при восстановлении сети.
 */
export function OfflineBanner() {
  const isOnline = useOnlineStatus();

  if (isOnline) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[300] offline-banner-enter">
      <div className="bg-status-warning text-white text-center py-2 px-4 text-[10px] font-black uppercase tracking-widest shadow-lg">
        📡 Нет подключения к сети — данные могут быть устаревшими
      </div>
    </div>
  );
}

/**
 * CTA-кнопка в нативном стиле Telegram — использует цвет кнопки из темы.
 */
export function TgButton({
  children,
  onClick,
  disabled,
  variant = 'primary',
  className = '',
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'danger';
  className?: string;
}) {
  const base = 'px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider active:scale-95 transition-all touch-target disabled:opacity-50';
  const variants = {
    primary: 'bg-tg-theme-button text-tg-theme-button-text shadow-lg',
    secondary: 'bg-tg-theme-sec-bg text-tg-theme-text border border-black/5 dark:border-white/5',
    danger: 'bg-status-error/10 text-status-error',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${base} ${variants[variant]} ${className}`}
    >
      {children}
    </button>
  );
}
